import * as zod from 'zod';

import { Step } from '@sb/remote-control/types';
import {
  DEFAULT_SERVO_CONFIG,
  ExternalControlServoConfig,
} from '@sb/routine-runner/Step/ExternalControl/Arguments';

export namespace ExternalControlStep {
  export const name = 'External Control';
  export const description =
    'Request control source for external control, run substeps, then release control source';
  export const isDecorator = true;
  export const librarySection = Step.LibrarySection.Control;
  export const librarySort = '1';
  export const argumentKind = 'ExternalControlStep';

  export const featureFlag = 'externalControlStep';

  /** Streaming modality the session expects. Mirrors the routine-runner
   * ExternalControl `streamType` and drives which control source / topic the
   * step uses (pose, joint streaming on stream_joints, or jointTrajectory via
   * the scaled_joint_trajectory_controller). */
  export const StreamType = zod.enum(['pose', 'joints', 'jointTrajectory']);
  export type StreamType = zod.infer<typeof StreamType>;

  /** Human-facing labels for the stream-type dropdown in the step editor. */
  export const StreamTypeLabels: Record<StreamType, string> = {
    pose: 'Pose',
    joints: 'Joints',
    jointTrajectory: 'Joint Trajectory',
  };

  /** Short, operator-facing explanation of each stream type, shown under the
   * dropdown. Describes what gets streamed, not how it is routed internally. */
  export const StreamTypeDescriptions: Record<StreamType, string> = {
    pose: 'Stream Cartesian tool poses to the robot.',
    joints: 'Stream joint positions to the robot.',
    jointTrajectory: 'Send a joint trajectory for the robot to follow.',
  };

  /** How the external client takes over. Mirrors the routine-runner
   * `controlledBy`. `api`, `rosHumble`, and `rosJazzy` are all exposed in the
   * UI; the external-control-bot maps each ROS distro to the matching bridge
   * conda env. */
  export const ControlledBy = zod.enum(['api', 'rosHumble', 'rosJazzy']);
  export type ControlledBy = zod.infer<typeof ControlledBy>;

  /** Human-facing labels for the "Controlled by" dropdown. */
  export const ControlledByLabels: Record<ControlledBy, string> = {
    api: 'API',
    rosHumble: 'ROS Humble',
    rosJazzy: 'ROS Jazzy',
  };

  /**
   * Optional trajectory-controller ServoJ tuning, applied when the session
   * streams joints through the online planner (`onlinePlannerStreaming`). Omitted
   * fields fall back to the server default. Temporary surface while the
   * online-planner streaming API stabilizes.
   *
   * The schema is owned by the routine-runner step (the layer that validates
   * it at load); re-exported here so every producer parses the same shape.
   */
  export const ServoConfig = ExternalControlServoConfig;
  export type ServoConfig = zod.infer<typeof ServoConfig>;

  /** Server-side ServoJ defaults; seeds the Streaming settings UI so its
   * controls start from the values an omitted field would fall back to. */
  export const ServoConfigDefaults: Required<ServoConfig> =
    DEFAULT_SERVO_CONFIG;

  /** Arguments to be passed to the routine runner */
  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    streamType: StreamType.default('pose'),
    controlledBy: ControlledBy.default('api'),
    servoConfig: ServoConfig.optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'ExternalControl',
      args: {
        streamType: args.streamType ?? 'pose',
        controlledBy: args.controlledBy ?? 'api',
        servoConfig: args.servoConfig,
      },
    };
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
    includeStepName,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      return null;
    }

    return includeStepName ? 'External Control' : '';
  };
}

ExternalControlStep satisfies Step.StepKindInfo;
